import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import {
    loadYoutubeTranscript,
    generateTimedQuestions,
    extractYouTubeId,
    type TimedQuestion,
    type TranscriptSegment,
} from '../api/youtube';

// ─── Helpers ────────────────────────────────────────────────────────────────

const YOUTUBE_ID_RE = /[A-Za-z0-9_-]{11}/;

function isHHMMSS(s: string): boolean {
    return /^\d{2}:\d{2}:\d{2}$/.test(s);
}

function makeEmptyQuestion(): TimedQuestion {
    return {
        id:           crypto.randomUUID(),
        questionText: '',
        options: [
            { answer: '', isCorrect: true  },
            { answer: '', isCorrect: false },
            { answer: '', isCorrect: false },
            { answer: '', isCorrect: false },
        ],
        timeStamp: '00:00:00',
        feedback:  { correct: 'Correct!', incorrect: 'Not quite — try again.' },
    };
}

function transcriptDurationSeconds(segments: TranscriptSegment[]): number | undefined {
    if (segments.length === 0) return undefined;
    const last = segments[segments.length - 1];
    if (!last) return undefined;
    const ms = last.offset + (last.duration ?? 0);
    return Math.floor(ms / 1000);
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface TimedQuestionsStepProps {
    /** YouTube URL or bare videoId. Drives transcript loading. */
    videoUrl:  string;
    questions: TimedQuestion[];
    onChange:  (q: TimedQuestion[]) => void;
    /** Default number of questions to ask the model for. */
    count?:    number;
    /**
     * Optional pre-fetched transcript segments. When supplied for the same
     * videoId we're showing, skip the network call entirely. The wizard's
     * URL step uses this to avoid a duplicate transcript fetch.
     */
    cachedTranscript?: TranscriptSegment[] | null;
}

export default function TimedQuestionsStep({
    videoUrl,
    questions,
    onChange,
    count = 5,
    cachedTranscript,
}: TimedQuestionsStepProps) {
    const videoId = extractYouTubeId(videoUrl);

    const [transcript, setTranscript] = useState<TranscriptSegment[] | null>(null);
    const [loadingTranscript, setLoadingTranscript] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Auto-load transcript whenever the videoId resolves to something new.
    useEffect(() => {
        if (!videoId) {
            setTranscript(null);
            return;
        }
        // Pre-cached for this exact videoId? Use it and skip the request.
        if (cachedTranscript && cachedTranscript.length > 0) {
            setTranscript(cachedTranscript);
            setError(null);
            setLoadingTranscript(false);
            return;
        }
        let cancelled = false;
        setLoadingTranscript(true);
        setError(null);
        loadYoutubeTranscript(videoId)
            .then(res => {
                if (cancelled) return;
                if (res.success && Array.isArray(res.data) && res.data.length > 0) {
                    setTranscript(res.data);
                    return;
                }
                setTranscript([]);
                if (res.success) {
                    // 2xx but no segments — surface only as the empty-state chip,
                    // not a red error alert.
                    return;
                }
                // Server reports a specific failure mode in res.message; show it.
                const detail = res.message ?? 'Failed to load transcript.';
                const kind = (res as { kind?: string }).kind;
                if (kind === 'not_configured') {
                    setError(`Transcript service is not configured on the server. ${detail}`);
                } else if (kind === 'unavailable') {
                    // Treat as empty state, not error.
                } else {
                    setError(detail);
                }
            })
            .catch(err => {
                if (!cancelled) setError(err?.message ?? 'Failed to load transcript.');
            })
            .finally(() => {
                if (!cancelled) setLoadingTranscript(false);
            });
        return () => { cancelled = true; };
    }, [videoId, cachedTranscript]);

    const canGenerate = !!transcript && transcript.length > 0 && !generating && !loadingTranscript;

    async function handleGenerate(): Promise<void> {
        if (!transcript || transcript.length === 0) return;
        setGenerating(true);
        setError(null);
        try {
            const res = await generateTimedQuestions({
                transcript,
                count,
                videoDurationSeconds: transcriptDurationSeconds(transcript),
            });
            if (res.success && res.data) {
                onChange(res.data.questions);
            } else {
                setError(res.message ?? 'Failed to generate questions.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to generate questions.');
        } finally {
            setGenerating(false);
        }
    }

    function updateQuestion(idx: number, patch: Partial<TimedQuestion>): void {
        onChange(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    }
    function updateOption(qIdx: number, oIdx: number, answer: string): void {
        onChange(questions.map((q, i) => i === qIdx
            ? { ...q, options: q.options.map((o, j) => j === oIdx ? { ...o, answer } : o) }
            : q));
    }
    function setCorrect(qIdx: number, oIdx: number): void {
        onChange(questions.map((q, i) => i === qIdx
            ? { ...q, options: q.options.map((o, j) => ({ ...o, isCorrect: j === oIdx })) }
            : q));
    }
    function removeQuestion(idx: number): void {
        onChange(questions.filter((_, i) => i !== idx));
    }
    function addQuestion(): void {
        onChange([...questions, makeEmptyQuestion()]);
    }

    // ── Render ──

    if (!videoId) {
        return (
            <Alert severity="info">
                Paste a YouTube URL in the previous step to enable transcript loading.
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>Timed Questions</Typography>
                <Typography variant="body2" color="text.secondary">
                    Questions pop up at their timestamps while the learner watches the video.
                    The badge can only be earned after every question is answered correctly.
                </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {loadingTranscript && (
                    <Chip
                        icon={<CircularProgress size={14} />}
                        label="Loading transcript…"
                        size="small"
                        variant="outlined"
                    />
                )}
                {!loadingTranscript && transcript && transcript.length > 0 && (
                    <Chip
                        color="success"
                        label={`Transcript: ${transcript.length} segments`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {!loadingTranscript && transcript && transcript.length === 0 && (
                    <Chip color="warning" label="No transcript available" size="small" variant="outlined" />
                )}
                <Button
                    startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                >
                    {questions.length > 0 ? 'Regenerate' : 'Generate Questions'}
                </Button>
                <Button color="secondary" startIcon={<AddIcon />} onClick={addQuestion}>
                    Add manually
                </Button>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {questions.length === 0 && !generating && (
                <Alert severity="info">
                    No questions yet. Use <strong>Generate Questions</strong> to have the AI propose timestamped
                    questions, or <strong>Add manually</strong> to start from scratch.
                </Alert>
            )}

            <Stack spacing={2}>
                {questions.map((q, qIdx) => {
                    const tsValid = q.timeStamp == null || isHHMMSS(q.timeStamp);
                    return (
                        <Paper key={q.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip label={`Q${qIdx + 1}`} size="small" />
                                    <TextField
                                        size="small"
                                        label="Timestamp"
                                        placeholder="HH:MM:SS"
                                        value={q.timeStamp ?? ''}
                                        onChange={e => updateQuestion(qIdx, { timeStamp: e.target.value || null })}
                                        error={!tsValid}
                                        helperText={tsValid ? undefined : 'Use HH:MM:SS'}
                                        sx={{ width: 140 }}
                                        inputProps={{ pattern: YOUTUBE_ID_RE.source }}
                                    />
                                    <Box flex={1} />
                                    <IconButton
                                        size="small"
                                        aria-label={`Remove question ${qIdx + 1}`}
                                        onClick={() => removeQuestion(qIdx)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Stack>

                                <TextField
                                    label="Question"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={q.questionText}
                                    onChange={e => updateQuestion(qIdx, { questionText: e.target.value })}
                                />

                                <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        Options (radio = correct answer)
                                    </Typography>
                                    {q.options.map((opt, oIdx) => (
                                        <Stack key={oIdx} direction="row" spacing={1} alignItems="center">
                                            <Radio
                                                size="small"
                                                checked={opt.isCorrect}
                                                onChange={() => setCorrect(qIdx, oIdx)}
                                                inputProps={{ 'aria-label': `Mark option ${oIdx + 1} correct` }}
                                            />
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={opt.answer}
                                                onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                                                placeholder={`Option ${oIdx + 1}`}
                                            />
                                        </Stack>
                                    ))}
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <TextField
                                        size="small"
                                        label="Feedback (correct)"
                                        fullWidth
                                        value={q.feedback.correct}
                                        onChange={e => updateQuestion(qIdx, { feedback: { ...q.feedback, correct: e.target.value } })}
                                    />
                                    <TextField
                                        size="small"
                                        label="Feedback (incorrect)"
                                        fullWidth
                                        value={q.feedback.incorrect}
                                        onChange={e => updateQuestion(qIdx, { feedback: { ...q.feedback, incorrect: e.target.value } })}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        </Stack>
    );
}
